import React from 'react';
import { cn } from '../../../utils/cn';

type Size = 'sm' | 'md' | 'lg';

const SIZES: Record<Size, string> = {
  sm: 'h-8 px-3 text-[13px] rounded-[8px]',
  md: 'h-10 px-4 text-sm rounded-[10px]',
  lg: 'h-12 px-5 text-[15px] rounded-[12px]',
};

const BASE =
  'inline-flex items-center justify-center gap-2 font-medium rt-focus transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed';

interface BaseBtnProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  size?: Size;
  icon?: React.ReactNode;
  loading?: boolean;
}

const withIcon = (
  icon: React.ReactNode | undefined,
  loading: boolean | undefined,
  children: React.ReactNode,
) => (
  <>
    {loading ? (
      <span
        className="inline-block h-4 w-4 rounded-full border-2 border-current border-t-transparent animate-spin"
        aria-hidden="true"
      />
    ) : (
      icon
    )}
    {children}
  </>
);

export const PrimaryButton = React.forwardRef<HTMLButtonElement, BaseBtnProps>(
  ({ size = 'md', icon, loading, className, children, disabled, ...rest }, ref) => (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      disabled={disabled || loading}
      className={cn(
        BASE,
        SIZES[size],
        'bg-[var(--rt-brand-600)] hover:bg-[var(--rt-brand-700)] text-white shadow-[0_1px_2px_rgba(15,23,21,.06)]',
        className,
      )}
      {...rest}
    >
      {withIcon(icon, loading, children)}
    </button>
  ),
);
PrimaryButton.displayName = 'PrimaryButton';

export const GhostButton = React.forwardRef<HTMLButtonElement, BaseBtnProps>(
  ({ size = 'md', icon, loading, className, children, disabled, ...rest }, ref) => (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      disabled={disabled || loading}
      className={cn(
        BASE,
        SIZES[size],
        'text-[var(--rt-ink-700)] hover:bg-black/[.04] dark:text-zinc-300 dark:hover:bg-white/5',
        className,
      )}
      {...rest}
    >
      {withIcon(icon, loading, children)}
    </button>
  ),
);
GhostButton.displayName = 'GhostButton';

export const SecondaryButton = React.forwardRef<HTMLButtonElement, BaseBtnProps>(
  ({ size = 'md', icon, loading, className, children, disabled, ...rest }, ref) => (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      disabled={disabled || loading}
      className={cn(
        BASE,
        SIZES[size],
        'bg-white text-[var(--rt-ink-800)] border border-[rgba(15,23,21,.1)] hover:bg-[var(--rt-ink-50)] dark:bg-[#1a201c] dark:text-white dark:border-white/10 dark:hover:bg-[#202622]',
        className,
      )}
      {...rest}
    >
      {withIcon(icon, loading, children)}
    </button>
  ),
);
SecondaryButton.displayName = 'SecondaryButton';

export const DangerButton = React.forwardRef<HTMLButtonElement, BaseBtnProps>(
  ({ size = 'md', icon, loading, className, children, disabled, ...rest }, ref) => (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      disabled={disabled || loading}
      className={cn(
        BASE,
        SIZES[size],
        'bg-[var(--rt-danger)] hover:brightness-95 text-white',
        className,
      )}
      {...rest}
    >
      {withIcon(icon, loading, children)}
    </button>
  ),
);
DangerButton.displayName = 'DangerButton';

export const OutlineButton = React.forwardRef<HTMLButtonElement, BaseBtnProps>(
  ({ size = 'md', icon, loading, className, children, disabled, ...rest }, ref) => (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      disabled={disabled || loading}
      className={cn(
        BASE,
        SIZES[size],
        'border border-[var(--rt-brand-500)] text-[var(--rt-brand-700)] hover:bg-[var(--rt-brand-50)] dark:text-[var(--rt-brand-300)] dark:border-[var(--rt-brand-400)] dark:hover:bg-[var(--rt-brand-500)]/10',
        className,
      )}
      {...rest}
    >
      {withIcon(icon, loading, children)}
    </button>
  ),
);
OutlineButton.displayName = 'OutlineButton';
